import type { SeoMeta } from '@dma/types';
import { InvalidMineralError } from '../errors/InvalidMineralError';

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export type MineralCategory = 'calcite' | 'aragonite' | 'kaolin' | 'other';
const VALID_CATEGORIES: MineralCategory[] = ['calcite', 'aragonite', 'kaolin', 'other'];

export interface MineralSpecification {
  label: string;
  value: string;
}

export interface MineralInput {
  id: string;
  name: string;
  slug: string;
  category: MineralCategory;
  description: string;
  specifications: MineralSpecification[];
  applications: string[];
  images: string[];
  seo: SeoMeta;
}

/**
 * Entity per the pattern in features/services and features/projects.
 * Real business rule: every catalog entry needs at least one image — a
 * mineral product listing with zero images is a content-entry mistake,
 * not a valid published state.
 */
export class Mineral {
  private constructor(public readonly props: MineralInput) {
    Object.freeze(this.props);
    Object.freeze(this);
  }

  public static create(input: MineralInput): Mineral {
    const name = input.name.trim();
    const slug = input.slug.trim();
    const description = input.description.trim();

    if (name.length < 2 || name.length > 150) {
      throw new InvalidMineralError('name must be between 2 and 150 characters.');
    }
    if (!SLUG_PATTERN.test(slug)) {
      throw new InvalidMineralError(`slug "${slug}" must be lowercase kebab-case.`);
    }
    if (!VALID_CATEGORIES.includes(input.category)) {
      throw new InvalidMineralError(`category must be one of: ${VALID_CATEGORIES.join(', ')}.`);
    }
    if (description.length === 0) {
      throw new InvalidMineralError('description must not be empty.');
    }
    if (input.images.length === 0) {
      throw new InvalidMineralError('images must contain at least one entry.');
    }
    if (input.images.some((image) => image.trim().length === 0)) {
      throw new InvalidMineralError('images must not contain empty entries.');
    }
    if (input.applications.some((application) => application.trim().length === 0)) {
      throw new InvalidMineralError('applications must not contain empty entries.');
    }

    return new Mineral({ ...input, name, slug, description });
  }
}
